import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import { AssetFinanceUrl } from "../../../config";

const base_url = AssetFinanceUrl.uri;

const STATUS_COLORS = {
  SUBMITTED: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const ASSET_STATUS_COLORS = {
  DRAFT: "default",
  PENDING_VERIF: "warning",
  PENDING_REVIEW: "warning",
  PENDING_VALUATION: "warning",
  APPROVED: "success",
  ACTIVE: "success",
  REJECTED: "error",
};

const VALUATION_METHOD_LABELS = {
  MARKET: "Market Approach",
  COMPARABLE_SALES: "Comparable Sales Approach",
  DEPRECIATED_REPLACEMENT: "Depreciated Replacement Cost Approach",
  INCOME: "Income Approach",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : "-");

// This page has no add/edit form of its own — it's a review queue, not a CRUD
// screen — but DynamicTableForJson always wires a FormComponent into its
// drawer/dialog, so a no-op stands in since Add/Edit are both disabled below.
const NoForm = () => null;

const collateralValuationTourSteps = [
  {
    target: ".dynamic-table-header",
    content:
      "Review collateral valuations submitted by valuer firms and approve or reject them here.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-actions",
    content: "Approve or reject a submitted valuation from this menu.",
    placement: "left",
  },
];

const columns = [
  { field: "Id", headerName: "Valuation ID", width: 100 },
  {
    field: "CustomerName",
    headerName: "Customer",
    flex: 1,
    valueGetter: (params) =>
      params.row.CustomerName || `#${params.row.CustomerId}`,
  },
  {
    field: "CustomerNationalId",
    headerName: "National ID / Phone",
    flex: 1,
    valueGetter: (params) =>
      [params.row.CustomerNationalId, params.row.CustomerPhone]
        .filter(Boolean)
        .join(" / ") || "-",
  },
  {
    field: "AssetTypeName",
    headerName: "Asset",
    flex: 1,
    valueGetter: (params) =>
      [params.row.AssetTypeName, params.row.SubTypeName]
        .filter(Boolean)
        .join(" - ") || `#${params.row.AssetId}`,
  },
  {
    field: "AssetStatus",
    headerName: "Asset Status",
    flex: 1,
    renderCell: (params) => {
      const status = params.row.AssetStatus || "DRAFT";
      return (
        <Chip
          label={String(status).replace(/_/g, " ")}
          size="small"
          color={ASSET_STATUS_COLORS[status] || "default"}
          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
        />
      );
    },
  },
  {
    field: "ValuerFirmName",
    headerName: "Valuer Firm",
    flex: 1,
    valueGetter: (params) =>
      params.row.ValuerFirmName || `#${params.row.ValuerFirmId}`,
  },
  {
    field: "ValuationMethod",
    headerName: "Method",
    flex: 1,
    valueGetter: (params) =>
      VALUATION_METHOD_LABELS[params.row.ValuationMethod] ||
      params.row.ValuationMethod,
  },
  {
    field: "IndicativeValue",
    headerName: "Indicative Value",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.IndicativeValue),
  },
  {
    field: "OpenMarketValue",
    headerName: "Open Market Value",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.OpenMarketValue),
  },
  {
    field: "ForcedSaleValue",
    headerName: "Forced Sale Value",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.ForcedSaleValue),
  },
  {
    field: "ValuationDate",
    headerName: "Valuation Date",
    flex: 1,
    valueGetter: (params) => formatDate(params.row.ValuationDate),
  },
  {
    field: "ValidUntil",
    headerName: "Valid Until",
    flex: 1,
    valueGetter: (params) => formatDate(params.row.ValidUntil),
  },
  {
    field: "IsEligibleForLoan",
    headerName: "Loan Eligible",
    flex: 1,
    renderCell: (params) => (
      <Chip
        label={params.row.IsEligibleForLoan ? "Eligible" : "Not Eligible"}
        size="small"
        color={params.row.IsEligibleForLoan ? "success" : "default"}
        sx={{ fontWeight: 600, fontSize: "0.7rem" }}
      />
    ),
  },
  {
    field: "ValuationStatus",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => {
      const status = params.row.ValuationStatus || "SUBMITTED";
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={String(status).replace(/_/g, " ")}
            size="small"
            color={STATUS_COLORS[status] || "default"}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
          {status === "REJECTED" && params.row.RejectionReason && (
            <Tooltip title={params.row.RejectionReason}>
              <WarningAmberOutlinedIcon fontSize="small" color="error" />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];

const ApprovedCollateralValuationTable = () => {
  const actions = {
    entity: "Collateral Pending Valuation",
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: false,
      permission: "CreatePermission",
    },
    showStatus: true,
    AprrovedRejectValuation: {
      key: "APPROVEVALUATION",
      button_name: "Approve/Reject",
      Show_Button: false,
      permission: "ActivatePermission",
      inputObjectName: "updateAssetValuationStatus",
      inputSubObjectName: "assetValuation",
    },

    dataObject: {
      ignoreFields: [
        "CreatedBy",
        "UpdatedBy",
        "UpdatedAt",
        "CompanyId",
        "ReportDocumentId",
        "Metadata",
      ],
      objectName: "collateralValuationByColumnValues",
      parameters: { valuationStatus: "APPROVED" },
      inputObjectName: "valuation",
    },
  };

  return (
    <Box m="20px">
      <DynamicTableForJson
        title="Approved Collateral Valuations"
        subtitle="Review approved valuations and manage them"
        FormComponent={NoForm}
        columns={columns}
        base_url={base_url}
        actions={actions}
        tourSteps={collateralValuationTourSteps}
      />
    </Box>
  );
};

export default ApprovedCollateralValuationTable;
